declare const Capacitor;
import type { BlobSaver8Plugin } from '../../../../blobsaver8/src/definitions';

export type DeviceFile = {
  name: string;
  size: number; // we need to use this if we have chunkFetcher, so we can show progress
  mTime: number; // files are the same if they have the same mTime and name, otherwise create a new file
  mtFrac: number; // fraction part of the mtime
  aliasName?: string; // alias when it's original name is already used
  relativeFolderPath?: string; // folder path from the root, example: "folder1/folder2"
  file?: File; // file handler from filepicker
  capacitorConvertFileSrc?: string; // Capacitor convertFileSrc can work with this, handy on android
  fileIndexInIOSAssetList?: number; // for ios, the index of the file in the list
};

export type IOSLoadPhotosType = { f_name: string; m_date: number; size: number; mt_frac: number };

export class BlobSaverAdapter {
  private blobSaverAuthToken?: string;

  public getPort = () => {
    return Capacitor.Plugins.BlobSaver8.getPort().then((res) => {
      return res.port;
    });
  };

  public getAuthToken = () => {
    if (this.blobSaverAuthToken) return Promise.resolve(this.blobSaverAuthToken);

    return Capacitor.Plugins.BlobSaver8.getAuthToken().then((res) => {
      this.blobSaverAuthToken = res.token;
      return res.token;
    });
  };

  public getCookie = (options: { url: string; key: string }) => {
    return (<BlobSaver8Plugin>Capacitor.Plugins.BlobSaver8).getCookie(options).then((res) => {
      return res[options.key];
    });
  };

  setCookie(options: { url: string; key: string; value: string; expires: Date }) {
    return (<BlobSaver8Plugin>Capacitor.Plugins.BlobSaver8).setCookie(options);
  }

  removeCookie(options: { url: string; key: string }) {
    return (<BlobSaver8Plugin>Capacitor.Plugins.BlobSaver8).removeCookie(options);
  }

  /**
   *
   * @param {id || name, close?, truncate?, overwrite: String("{base}, {ext}, {x}")} options -
   * @param {Blob} blob - File data
   * @returns {id?, name?} resp
   */
  uploadFile = (
    options: {
      id?: any;
      name?: string;
      close?: boolean;
      truncate?: boolean;
      overwrite?: string;
    },
    blob: Blob = new Blob([])
  ) => {
    return this.getPort().then((port) => {
      return this.getAuthToken().then((auth) => {
        const queryParams = new URLSearchParams(options as any).toString();
        return fetch('http://localhost:' + port + '?' + queryParams, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/octet-stream',
            Authorization: auth,
          },
          body: blob,
          mode: 'cors', // no-cors, *cors, same-origin
          cache: 'no-cache', // *default, no-cache, reload, force-cache, only-if-cached
          credentials: 'same-origin', // include, *same-origin, omit
          redirect: 'follow', // manual, *follow, error
          referrerPolicy: 'no-referrer', // no-referrer, *no-referrer-when-downgrade, origin, origin-when-cross-origin, same-origin, strict-origin, strict-origin-when-cross-origin, unsafe-url
        }).then((resp) => {
          return resp.json().then((result) => {
            if (result.error) {
              // fetch does not throw error on normally responded server error
              throw new Error(result.message);
            } else {
              return result;
            }
          });
        });
      });
    });
  };

  /**
   * Load all asset resources from photos on iphone. Calling this again resets the initialization.
   * After this, all files available for fetching their binary data through blobsaver server: fetchChunkForIOSAsset
   * All chunks contains a maximum 1 megabyte data (depends on device?).
   */
  initIOSPhotosLibraryForFetching: () => Promise<DeviceFile[]> = () => {
    return this.getPort().then((port) => {
      return this.getAuthToken().then((auth) => {
        return fetch('http://localhost:' + port + '/load-photos', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/octet-stream',
            Authorization: auth,
          },
          mode: 'cors', // no-cors, *cors, same-origin
          cache: 'no-cache', // *default, no-cache, reload, force-cache, only-if-cached
          credentials: 'same-origin', // include, *same-origin, omit
          redirect: 'follow', // manual, *follow, error
          referrerPolicy: 'no-referrer', // no-referrer, *no-referrer-when-downgrade, origin, origin-when-cross-origin, same-origin, strict-origin, strict-origin-when-cross-origin, unsafe-url
        }).then((resp) => {
          return resp.json().then((result) => {
            if (result.error) {
              // fetch does not throw error on normally responded server error
              //throw new Error(result.message);
              return null;
            } else {
              console.log(result);
              let serverRes: IOSLoadPhotosType[] = result.assets;
              return serverRes.map((e, fileIndexInIOSAssetList) =>
                this._convertIOSFilePhotosRecordToDeviceFileRecord(e, fileIndexInIOSAssetList)
              );
            }
          });
        });
      });
    });
  };

  private savedChunks = {};
  public async getFileFetcherForIOS(fileAssetIndex: number, chunkSize: number) {
    let streamEnded = false;
    let data = new Blob();
    var maximumLoop = 10;

    //console.log("BACKUPDEBUG check chunk size", data.size, chunkSize)
    while (data.size < chunkSize && !streamEnded) {
      let cachedBlob: ArrayBuffer = this.savedChunks[fileAssetIndex];
      maximumLoop--;
      if (maximumLoop == 0) throw 'This is not bueno';

      //console.log("BACKUPDEBUG do we have blob?", cachedBlob, cachedBlob?.byteLength)
      if (cachedBlob && cachedBlob.byteLength > 0) {
        var reqPartSize = chunkSize - data.size;

        if (cachedBlob.byteLength <= reqPartSize) {
          //console.log("BACKUPDEBUG get all blob", cachedBlob.byteLength)
          data = new Blob([data, cachedBlob]);
          delete this.savedChunks[fileAssetIndex];
        } else {
          //console.log("BACKUPDEBUG get blob slice", reqPartSize)
          data = new Blob([data, cachedBlob.slice(0, reqPartSize)]);
          this.savedChunks[fileAssetIndex] = cachedBlob.slice(reqPartSize);
          //console.log("BACKUPDEBUG blob now", data.size, "stored blob", this.savedChunks[fileAssetIndex].byteLength)
        }
      } else {
        //console.log("BACKUPDEBUG get from server")
        let fetchData = await this.fetchChunkForIOSAsset(fileAssetIndex);
        //console.log("BACKUPDEBUG got from server", fetchData)
        this.savedChunks[fileAssetIndex] = fetchData.chunk;
        streamEnded = fetchData.done;
      }
    }

    let buffer = await data.arrayBuffer();
    return { chunk: buffer, done: streamEnded };
  }

  /**
   *
   * @param assetIndex
   * @returns
   */
  fetchChunkForIOSAsset: (assetIndex: number) => Promise<{ chunk: ArrayBuffer; done: boolean }> = (
    assetIndex
  ) => {
    const queryParams = new URLSearchParams({ index: assetIndex + '' }).toString();
    //console.log("BACKUPDEBUG get this from server", assetIndex)
    return this.getPort().then((port) => {
      return this.getAuthToken().then((auth) => {
        return fetch('http://localhost:' + port + '/fetch-next-asset' + '?' + queryParams, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/octet-stream',
            Authorization: auth,
          },
          mode: 'cors', // no-cors, *cors, same-origin
          cache: 'no-cache', // *default, no-cache, reload, force-cache, only-if-cached
          credentials: 'same-origin', // include, *same-origin, omit
          redirect: 'follow', // manual, *follow, error
          referrerPolicy: 'no-referrer', // no-referrer, *no-referrer-when-downgrade, origin, origin-when-cross-origin, same-origin, strict-origin, strict-origin-when-cross-origin, unsafe-url
        }).then((resp) => {
          if (resp.headers.get('Content-type') == 'application/json') {
            return resp.json().then((result) => {
              console.log('json msg', result);
              if (result.error) {
                // fetch does not throw error on normally responded server error
                throw new Error(result.message);
              } else {
                return {
                  chunk: new ArrayBuffer(0),
                  done: true,
                };
              }
            });
          }

          return resp.arrayBuffer().then((chunk) => {
            //console.log("BACKUPDEBUG got chunk", chunk, !!resp.headers.get('X-Done'))
            return {
              chunk,
              done: !!resp.headers.get('X-Done'),
            };
          });
        });
      });
    });
  };

  public resetIOSAssetFetch: () => Promise<void> = () => {
    //console.log("BACKUPDEBUG get this from server", assetIndex)
    return this.getPort().then((port) => {
      return this.getAuthToken().then((auth) => {
        return fetch('http://localhost:' + port + '/reset-fetch', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/octet-stream',
            Authorization: auth,
          },
          mode: 'cors', // no-cors, *cors, same-origin
          cache: 'no-cache', // *default, no-cache, reload, force-cache, only-if-cached
          credentials: 'same-origin', // include, *same-origin, omit
          redirect: 'follow', // manual, *follow, error
          referrerPolicy: 'no-referrer', // no-referrer, *no-referrer-when-downgrade, origin, origin-when-cross-origin, same-origin, strict-origin, strict-origin-when-cross-origin, unsafe-url
        }).then((resp) => {
          return;
        });
      });
    });
  };

  _convertIOSFilePhotosRecordToDeviceFileRecord: (
    f: IOSLoadPhotosType,
    fileIndexInIOSAssetList: number
  ) => DeviceFile = (f, fileIndexInIOSAssetList) => {
    let d: DeviceFile = {
      name: f.f_name,
      size: f.size,
      mTime: f.m_date,
      mtFrac: f.mt_frac,
      fileIndexInIOSAssetList,
    };

    return d;
  };

  createFile = (name: string, blob: Blob, overwrite?: string, close?: boolean) => {
    return this.uploadFile({ name, close, overwrite }, blob);
  };

  appendFile = (id: any, blob: Blob, close?: boolean) => {
    return this.uploadFile({ id, close }, blob);
  };

  truncateFile = (id: any) => {
    return this.uploadFile({ id, truncate: true });
  };

  closeFile = (id: any) => {
    return this.uploadFile({ id, close: true });
  };

  getFileWriter = (
    fileName: string,
    overwrite?: string,
    convertPayload?: (blob: Blob) => Promise<Blob>
  ) => {
    return this.createFile(fileName, new Blob([]), overwrite).then((result) => {
      return {
        write: (blob: Blob) => {
          if (convertPayload) {
            return convertPayload(blob).then((payload) => {
              return this.appendFile(result.id, payload);
            });
          } else {
            return this.appendFile(result.id, blob);
          }
        },
        close: () => {
          return this.closeFile(result.id);
        },
        truncate: (size: number) => {
          return this.truncateFile(result.id);
        },
        getName: () => {
          return result.name;
        },
        getId: () => {
          return result.id;
        },
        getPath: () => {
          return result.path;
        },
      };
    });
  };

  /**
   * Android specific
   * @returns
   */
  public getAndroidPhotosLibrary = (): Promise<DeviceFile[]> => {
    return Capacitor.Plugins.BlobSaver8.getFilePathList().then((res: object) => {
      console.log('res object', res);
      if (res['fileAccessGranted'] === false) return null;
      return res['deviceFiles'] ?? [];
    });
  };

  public getBrowserSuppportedLibraryPickerResult = async (): Promise<DeviceFile[]> => {
    return window['showDirectoryPicker']()
      .then(async (dir) => {
        console.log('showDirectoryPicker');
        var files: DeviceFile[] = [];
        // push all files to array recursively
        async function getFiles(dir, relativePath = '') {
          for await (let [name, handle] of dir.entries()) {
            if (handle.kind === 'file') {
              let f = await handle.getFile();
              files.push({
                name: f.name,
                size: f.size,
                mTime: f.lastModified,
                mtFrac: (f.lastModified % 1000) + 1000,
                file: f,
                relativeFolderPath: relativePath,
              });
            }
            if (handle.kind === 'directory') {
              console.log('read dir', handle.name);
              await getFiles(handle, relativePath ? relativePath + '/' + handle.name : handle.name);
            }
          }
        }
        await getFiles(dir, dir.name);
        console.log('files', files);
        return files;
      })
      .catch((e) => {
        console.error('showDirectoryPicker error', e);
        return null;
      });
  };
}
